<script setup lang="ts">
  import { useImage } from '@/composables/useImage'
  import type { AnchorNavigationTask } from '@/types/Task'
  import type { LongformText } from '@/types/LongformText'
  import SignatureIcon from '@/components/icons/SignatureIcon.vue'

  import ImageWithCaption from '@/components/common/ImageWithCaption.vue'
  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
  import Subtasks from '@/components/task/Subtasks.vue'
  import Typography from '@/components/common/Typography.vue'
  import LongformTextBlock from '@/components/LongformTextBlock.vue'

  const props = defineProps<AnchorNavigationTask & {
    superTitle?: string,
  }>()

  const { src, altText, copyright, caption } = useImage({
    imageId: props.image?.destinationContentId ?? '',
    svgId: props.svg?.destinationContentId ?? '',
  })
</script>

<template>
  <div class="anchor-task-wrapper">
    <div
      :class="['anchor-task', { 'no-image' : !src }]"
      data-track-context="ankeroppgave"
    >
      <div class="left">
        <div class="content-wrapper">
          <Typography
            v-if="superTitle"
            :id="superTitle"
            variant="label"
            class="section-header-super-title"
          >
            {{ superTitle }}
          </Typography>
          <Typography
            v-if="title"
            variant="h2"
            class="section-header"
          >
            {{ title }}
          </Typography>
          <SignatureIcon
            v-if="isNoAidTask"
            class="icon"
          />
          <div
            v-if="description"
            class="description"
          >
            <RichTextRenderer :rich-text="description" />
          </div>

          <Subtasks
            :subtasks="subtasks"
            variant="number"
          />

          <RichTextRenderer
            v-if="descriptionAfterSubtasks"
            :rich-text="descriptionAfterSubtasks"
          />
        </div>
      </div>

      <div
        v-if="src || rightColumnRichText"
        class="right"
      >
        <ImageWithCaption
          v-if="src && !rightColumnRichText"
          :src="src"
          :alt-text="altText"
          :caption="caption"
          :copyright="copyright"
          :object-fit="'cover'"
          :aspect-ratio="'3/2'"
          class="image"
        />
        <LongformTextBlock
          v-if="rightColumnRichText"
          :element="{ richText: rightColumnRichText } as LongformText"
        />
      </div>
    </div>
  </div>
  <div id="audio-embed-outlet" />
</template>

<style scoped lang="scss">
  :deep(.longform-text) {
    margin: 0;
    overflow: auto;
    height: 100%;
  }

  .anchor-task-wrapper {
    display: grid;
    grid-template-rows: auto 1fr auto;
    grid-template-areas: "header"
      "main"
      "footer";

    main {
      grid-area: main;

      @media (width <= 959px) {
        overflow-y: auto;
      }
    }
  }

  .icon {
    margin-bottom: 8px;
  }

  .anchor-task {
    display: flex;
    overflow-y: auto;
    height: 100%;

    @include vgs-media-mobile-and-tablet {
      flex-direction: column;
    }

    .left {
      flex: 16;
      max-width: $vgs-max-width-content;
    }

    .content-wrapper {
      margin: 0 40px 0 48px;
    }

    .right {
      height: 100%;
      flex: 15;

      :deep(.longform-text:has(.player-wrapper)) {
        overflow: hidden;
      }

      @include video-overlay;
    }

    .right-column-rich-text {
      background-color: var(--color-yellow-20);
    }

    .stretch {
      padding: 0;

      :deep(.image-with-caption-container) {
        height: 100%;
      }
    }
  }

  .description {
    :deep(p:first-of-type) {
      margin-top: 0;
    }

    :deep(p:last-of-type) {
      margin-bottom: 24px;
    }
  }

  .image {
    max-height: 100%;
    margin-left: auto;
    margin-right: auto;
    width: initial;
  }

  #audio-embed-outlet {
    position: fixed;
    width: 100%;
    border-radius: 6px;
    bottom: 0;
    left: 0;
    z-index: 20;
  }

  :deep(.media-controls-wrapper) {
    border-radius: 6px;
    margin: 8px;
  }
</style>
